import { QuestionStatus } from '@exam-management/graphql';
import {
  BarChartIcon,
  Flex,
  FormControl,
  FormHelperText,
  Text,
  FormLabel,
  FormNumberInput,
  FormRadio,
  FormRadioSelection,
  IconButton,
  Radio,
  RadioGroup,
  ReadonlyField,
} from '@shared/components';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  EDITABLE_ITEM_QUESTION_B_INDEX_STATUSES,
  EDITABLE_ITEM_QUESTION_P_STAT_STATUSES,
} from '../..';
import { PValueField, DifficultyText, DEFAULT_CRITERIA } from '../../components';
import { getDifficultyCriteria } from '../../utils';
import type { InformationSectionType } from '../information-section';
import { QUESTION_TYPE_OPTIONS } from '../information-section/constants';
export interface InformationFormSectionProps {
  questionStatus: QuestionStatus;
  onClick?: () => void;
}

export default function InformationFormSection(props: Readonly<InformationFormSectionProps>) {
  const { questionStatus, onClick } = props;
  const formContext = useFormContext<InformationSectionType>();
  const pValueErrorMessages = formContext.formState.errors?.information?.pValue?.selected
    ?.message as string;

  const pStatsErrorMessages = formContext.formState.errors?.information?.pStats
    ? (
        formContext.formState.errors?.information?.pStats as unknown as {
          message: string;
        }
      ).message
    : '';
  const pStatsValueErrorMessages = formContext.formState.errors?.information?.pValue?.pStat;

  const rawPStat = formContext.watch('information.pValue.pStat');

  const pStat = typeof rawPStat === 'number' ? rawPStat.toFixed(2) : '-';

  const currentCriteria = useMemo(() => {
    const normalizedPStat = typeof rawPStat === 'number' ? rawPStat : null;
    return getDifficultyCriteria(DEFAULT_CRITERIA, normalizedPStat);
  }, [rawPStat]);

  const canEditPStat = questionStatus === QuestionStatus.Unpublished;

  const shouldDisplayPStat = EDITABLE_ITEM_QUESTION_P_STAT_STATUSES.includes(questionStatus);
  const shouldDisplayBIndex = EDITABLE_ITEM_QUESTION_B_INDEX_STATUSES.includes(questionStatus);

  return (
    <Flex
      sx={(theme) => ({
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        rowGap: theme.spacing(3),
        columnGap: theme.spacing(6),
      })}
    >
      <FormRadioSelection
        name='information.calculated'
        label='ประเภทข้อสอบ'
        options={QUESTION_TYPE_OPTIONS}
        required
        row
        sx={{
          minWidth: 196,
          '& .MuiFormGroup-root': {
            paddingTop: (theme) => theme.spacing(1),
          },
        }}
      />
      <FormControl>
        <FormLabel required>ค่าความยาก (P)</FormLabel>
        <RadioGroup
          row
          sx={(theme) => ({ gap: theme.spacing(4), mt: theme.spacing(1), flexWrap: 'wrap' })}
        >
          <Flex sx={{ alignItems: 'flex-start' }}>
            <FormRadio
              name='information.pValue.selected'
              value='pInitial'
              label='P Initial'
              sx={{
                width: '100%',
                maxWidth: 96,
              }}
            />
            <PValueField
              placeholder='ระบุค่า P'
              controllerProps={{
                name: 'information.pValue.pInitial',
                control: formContext.control,
              }}
              sx={{
                maxWidth: 128,
              }}
            />
          </Flex>
          {shouldDisplayPStat ? (
            <Flex
              sx={{
                flexDirection: 'column',
              }}
            >
              <Flex
                sx={{
                  alignItems: 'flex-start',
                  minWidth: '254px',
                }}
              >
                {canEditPStat ? (
                  <FormRadio
                    name='information.pValue.selected'
                    value='pStat'
                    label='P Stat'
                    sx={{
                      width: '100%',
                      minWidth: 74,
                    }}
                  />
                ) : (
                  <Radio
                    name='information.pValue.selected'
                    value='pStat'
                    label='P Stat'
                    sx={{
                      width: '100%',
                      minWidth: 74,
                    }}
                    disabled
                  />
                )}
                <ReadonlyField
                  content={
                    <Flex
                      sx={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text>{pStat}</Text>
                      <DifficultyText criteria={currentCriteria} />
                    </Flex>
                  }
                  sx={{
                    minWidth: '120px',
                  }}
                />

                <Flex
                  sx={() => ({
                    alignSelf: 'center',
                  })}
                >
                  <IconButton onClick={onClick}>
                    <BarChartIcon
                      sx={(theme) => ({
                        fontSize: '28px',
                        color: theme.palette.text.disabled,
                      })}
                    />
                  </IconButton>
                </Flex>
              </Flex>
              {pStatsValueErrorMessages ? (
                <FormHelperText error>{pStatsValueErrorMessages.message}</FormHelperText>
              ) : null}
            </Flex>
          ) : null}
        </RadioGroup>
        <FormHelperText error>{pValueErrorMessages ?? pStatsErrorMessages}</FormHelperText>
      </FormControl>
      {shouldDisplayBIndex ? (
        <FormNumberInput
          name='information.bIndex'
          label='ค่า B-Index'
          allowNegative
          placeholder='ระบุค่า B-index'
          maxDecimalPlaces={2}
          sx={{
            width: '100%',
            maxWidth: 132,
          }}
        />
      ) : null}
    </Flex>
  );
}
